import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { cx } from '../lib/cx'
import { Icon } from './Icon'
import './BottomSheet.css'

export interface BottomSheetProps {
  open: boolean
  /** Called on Escape, on the close button and on a tap outside the sheet. */
  onClose: () => void
  title: string
  description?: string
  children: ReactNode
  /** Actions pinned to the bottom of the sheet. */
  footer?: ReactNode
  /** What gets focus when the sheet opens; the sheet itself by default. */
  initialFocusRef?: RefObject<HTMLElement | null>
}

/** Matches --motion-slow: sheets slide up or down in 400 ms. */
export const SHEET_MOTION_MS = 400

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
}

/**
 * A modal sheet that slides up from the bottom (motion-slow). Focus is trapped
 * inside while it is open, Escape closes it, the page behind is inert, and focus
 * goes back to whatever opened it.
 */
export function BottomSheet({ open, onClose, title, description, children, footer, initialFocusRef }: BottomSheetProps) {
  const { t } = useTranslation()
  const titleId = useId()
  const descriptionId = useId()
  const sheetRef = useRef<HTMLDivElement>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)
  const [mounted, setMounted] = useState(open)
  const [shown, setShown] = useState(false)

  // Mount, then show on the next frame so the slide-up transition runs; on close,
  // hide first and unmount once the slide-down has finished.
  if (open && !mounted) setMounted(true)
  if (!open && shown) setShown(false)
  useEffect(() => {
    if (open) {
      const frame = requestAnimationFrame(() => setShown(true))
      return () => cancelAnimationFrame(frame)
    }
    if (!mounted) return
    const timer = setTimeout(() => setMounted(false), prefersReducedMotion() ? 100 : SHEET_MOTION_MS)
    return () => clearTimeout(timer)
  }, [open, mounted])

  // Focus in, page behind inert, focus back out.
  useEffect(() => {
    if (!open || !mounted) return
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const target = initialFocusRef?.current ?? sheetRef.current
    target?.focus()
    const appRoot = document.getElementById('root')
    appRoot?.setAttribute('inert', '')
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      appRoot?.removeAttribute('inert')
      document.body.style.overflow = previousOverflow
      const back = returnFocusRef.current
      if (back && document.contains(back)) back.focus()
    }
  }, [open, mounted, initialFocusRef])

  if (!mounted) return null

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation()
      onClose()
      return
    }
    if (event.key !== 'Tab' || !sheetRef.current) return
    const focusable = Array.from(sheetRef.current.querySelectorAll<HTMLElement>(FOCUSABLE))
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (!first || !last) {
      event.preventDefault()
      return
    }
    const active = document.activeElement
    if (event.shiftKey && (active === first || active === sheetRef.current)) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && active === last) {
      event.preventDefault()
      first.focus()
    }
  }

  return createPortal(
    <div className={cx('sheet-layer', shown && 'sheet-layer--shown')}>
      <div className="sheet-scrim" aria-hidden="true" onClick={onClose} />
      <div
        ref={sheetRef}
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <div className="sheet__handle" aria-hidden="true" />
        <div className="sheet__header">
          <h2 id={titleId} className="t-heading">
            {title}
          </h2>
          <button type="button" className="sheet__close" onClick={onClose}>
            <Icon name="close" label={t('kit.sheet.close')} />
          </button>
        </div>
        {description && (
          <p id={descriptionId} className="sheet__description t-muted">
            {description}
          </p>
        )}
        <div className="sheet__body">{children}</div>
        {footer && <div className="sheet__footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  )
}
